<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId } from 'vue'

import { usePopoverPlacement } from '@/composables/ui/use-popover-placement.js'

import BaseIcon from './BaseIcon.vue'
import type { IconName } from '@/constants/app/icon.constant.js'

/**
 * A menu: a trigger (the slot) and a list of actions.
 *
 * The WAI-ARIA menu button pattern, which is more than a dropdown: the trigger carries
 * `aria-haspopup` and `aria-expanded`; opening moves focus to the first item; arrow keys
 * move between items; Escape and clicking outside close it and return focus to the
 * trigger. Each of those is a thing a keyboard-only operator notices on the first use.
 *
 * Used for the avatar menu now and row-action menus later — the same component, so the
 * keyboard behaviour cannot drift between them.
 *
 * Sized and placed by `usePopoverPlacement`: `w-max min-w-56` so a long action label is
 * read rather than truncated, flipping above its trigger near the bottom of a long table
 * and hanging off the right edge near the side of the window. It was pinned `right-0`
 * below its trigger with no alternative, so the actions on the last rows of any list
 * opened below the fold.
 */
export interface MenuItem {
  readonly id: string
  readonly label: string
  readonly icon?: IconName
  readonly tone?: 'default' | 'danger'
}

defineProps<{ items: readonly MenuItem[]; label: string }>()
const emit = defineEmits<{ select: [string] }>()

const open = ref(false)
const root = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const list = ref<HTMLElement | null>(null)

// The row-action menu on the last rows of a long table had the same problem as the
// rows-per-page select: `absolute … mt-1.5` with no alternative, so the actions opened
// below the fold. `list` is both the menu and the measured panel here.
const { side, maxHeight, align, maxWidth } = usePopoverPlacement(() => root.value, list)

const menuStyle = computed(() => ({
  ...(maxHeight.value === null ? {} : { maxHeight: `${String(maxHeight.value)}px` }),
  ...(maxWidth.value === null ? {} : { maxWidth: `${String(maxWidth.value)}px` }),
}))
const menuId = useId()

function itemsIn(): HTMLElement[] {
  return [...(list.value?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])]
}

async function show(): Promise<void> {
  open.value = true
  document.addEventListener('pointerdown', onOutside)
  await nextTick()
  itemsIn()[0]?.focus()
}

function hide(returnFocus = true): void {
  open.value = false
  document.removeEventListener('pointerdown', onOutside)
  if (returnFocus) trigger.value?.focus()
}

function onOutside(event: PointerEvent): void {
  if (root.value !== null && !root.value.contains(event.target as Node)) hide(false)
}

function move(delta: number): void {
  const items = itemsIn()
  const index = items.indexOf(document.activeElement as HTMLElement)
  const next = items[(index + delta + items.length) % items.length]

  next?.focus()
}

function choose(id: string): void {
  hide()
  emit('select', id)
}

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onOutside)
})
</script>

<template>
  <div
    ref="root"
    class="relative"
  >
    <button
      ref="trigger"
      type="button"
      :aria-label="label"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-controls="menuId"
      class="rounded-full"
      @click="open ? hide() : show()"
      @keydown.down.prevent="show()"
    >
      <slot />
    </button>

    <Transition name="menu">
      <div
        v-if="open"
        :id="menuId"
        ref="list"
        role="menu"
        tabindex="-1"
        :aria-label="label"
        :style="menuStyle"
        :data-side="side"
        :class="[
          // `overflow-x-hidden` explicitly: setting only `overflow-y` makes the x axis
          // compute to `auto` per spec, which is a horizontal scrollbar nobody asked for.
          'absolute z-30 w-max min-w-56 overflow-x-hidden overflow-y-auto overscroll-contain rounded-md',
          'border border-border-subtle bg-raised py-1 shadow-overlay',
          side === 'above' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          // `right-0` unless placement actually measured and chose otherwise. This menu
          // was unconditionally right-aligned, and it is used by the avatar button in the
          // top-right corner — so an UNMEASURED panel defaulting to `left-0` would grow
          // off the side of the window, turning a fallback into a worse bug than the one
          // being fixed. `maxWidth` is non-null only once a real measurement happened.
          maxWidth !== null && align === 'start' ? 'left-0' : 'right-0',
        ]"
        @keydown.escape.prevent="hide()"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.tab="hide(false)"
      >
        <div
          v-if="$slots.header"
          class="border-b border-border-hairline px-3 py-2"
        >
          <slot name="header" />
        </div>
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          role="menuitem"
          tabindex="-1"
          :class="[
            'flex w-full items-center gap-2.5 px-3 py-2 text-left text-body transition-colors duration-(--motion-fast)',
            item.tone === 'danger' ? 'text-danger hover:bg-danger-tint' : 'text-ink hover:bg-sunken',
          ]"
          @click="choose(item.id)"
        >
          <BaseIcon
            v-if="item.icon !== undefined"
            :name="item.icon"
            :size="16"
          />
          {{ item.label }}
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition:
    opacity var(--motion-fast) var(--motion-ease),
    transform var(--motion-fast) var(--motion-ease);
}
.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
/* Rising out of the trigger rather than dropping into it, when the menu opens upward. */
.menu-enter-from[data-side='above'],
.menu-leave-to[data-side='above'] {
  transform: translateY(4px);
}
</style>
